import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
	Box,
	Button,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	alpha,
} from '@mui/material';
import {
	ChevronLeft,
	ChevronRight,
	KeyboardDoubleArrowLeft,
	KeyboardDoubleArrowRight,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Util } from '../../util/Util';

export type DepositPickItem = {
	id: number;
	amount: number;
	date?: string | null;
	mode_name?: string;
	kind?: string;
	client?: string;
	payee?: string;
	category?: string;
	reference?: string;
	code?: string;
	issuee?: string;
	batch?: string;
	client_id?: number;
	depot?: string | null;
	depot_id?: number | null;
};

type Column = {
	key: string;
	header: string;
	align?: 'left' | 'right';
	render: (item: DepositPickItem) => ReactNode;
};

type Props = {
	title: string;
	hint?: string;
	availableLabel: string;
	selectedLabel: string;
	emptyAvailable: string;
	emptySelected: string;
	available: DepositPickItem[];
	selected: DepositPickItem[];
	onAdd: (items: DepositPickItem[]) => void;
	onRemove: (items: DepositPickItem[]) => void;
	columns: Column[];
};

const itemKey = (item: DepositPickItem) => `${item.kind || 'row'}-${item.id}`;

const pruneKeys = (current: Set<string>, items: DepositPickItem[]) => {
	const allowed = new Set(items.map(itemKey));
	const next = new Set<string>();
	let changed = false;
	current.forEach((key) => {
		if (allowed.has(key)) next.add(key);
		else changed = true;
	});
	return changed ? next : current;
};

const PickTable = ({
	label,
	items,
	empty,
	columns,
	highlighted,
	onToggle,
	onMove,
}: {
	label: string;
	items: DepositPickItem[];
	empty: string;
	columns: Column[];
	highlighted: Set<string>;
	onToggle: (item: DepositPickItem) => void;
	onMove: (item: DepositPickItem) => void;
}) => (
	<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
		<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
			{label} ({items.length})
		</Typography>
		<TableContainer
			component={Paper}
			elevation={0}
			sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxHeight: 280 }}
		>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow>
						{columns.map((column) => (
							<TableCell key={column.key} align={column.align} sx={{ fontWeight: 700 }}>
								{column.header}
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{items.length === 0 ? (
						<TableRow>
							<TableCell colSpan={columns.length} align="center">
								<Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ py: 3 }}>
									{empty}
								</Typography>
							</TableCell>
						</TableRow>
					) : (
						items.map((item) => {
							const key = itemKey(item);
							const isHighlighted = highlighted.has(key);
							return (
								<TableRow
									key={key}
									hover
									selected={isHighlighted}
									onClick={() => onToggle(item)}
									onDoubleClick={() => onMove(item)}
									sx={{
										cursor: 'pointer',
										'&.Mui-selected': {
											bgcolor: (theme) => alpha(theme.palette.primary.main, 0.14),
										},
										'&.Mui-selected:hover': {
											bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2),
										},
									}}
								>
									{columns.map((column) => (
										<TableCell key={column.key} align={column.align}>
											{column.render(item)}
										</TableCell>
									))}
								</TableRow>
							);
						})
					)}
				</TableBody>
			</Table>
		</TableContainer>
	</Box>
);

const DepositPickList = ({
	title,
	hint,
	availableLabel,
	selectedLabel,
	emptyAvailable,
	emptySelected,
	available,
	selected,
	onAdd,
	onRemove,
	columns,
}: Props) => {
	const { t } = useTranslation();
	const [highlightLeft, setHighlightLeft] = useState<Set<string>>(new Set());
	const [highlightRight, setHighlightRight] = useState<Set<string>>(new Set());

	useEffect(() => {
		setHighlightLeft((current) => pruneKeys(current, available));
	}, [available]);

	useEffect(() => {
		setHighlightRight((current) => pruneKeys(current, selected));
	}, [selected]);

	const toggle = (side: 'left' | 'right', item: DepositPickItem) => {
		const key = itemKey(item);
		const setter = side === 'left' ? setHighlightLeft : setHighlightRight;
		setter((current) => {
			const next = new Set(current);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	};

	const highlightedAvailable = useMemo(
		() => available.filter((item) => highlightLeft.has(itemKey(item))),
		[available, highlightLeft],
	);
	const highlightedSelected = useMemo(
		() => selected.filter((item) => highlightRight.has(itemKey(item))),
		[selected, highlightRight],
	);

	const addHighlighted = () => {
		if (!highlightedAvailable.length) return;
		onAdd(highlightedAvailable);
		setHighlightLeft(new Set());
	};

	const removeHighlighted = () => {
		if (!highlightedSelected.length) return;
		onRemove(highlightedSelected);
		setHighlightRight(new Set());
	};

	const addAll = () => {
		if (!available.length) return;
		onAdd(available);
		setHighlightLeft(new Set());
	};

	const removeAll = () => {
		if (!selected.length) return;
		onRemove(selected);
		setHighlightRight(new Set());
	};

	const transferButtons = (
		<Stack
			spacing={1}
			alignItems="stretch"
			justifyContent="center"
			sx={{
				minWidth: { xs: '100%', md: 132 },
				width: { xs: '100%', md: 132 },
				alignSelf: { md: 'center' },
				position: { md: 'sticky' },
				top: { md: 24 },
				py: { xs: 0.5, md: 0 },
			}}
		>
			<Button
				size="small"
				variant="outlined"
				onClick={addAll}
				disabled={!available.length}
				startIcon={<KeyboardDoubleArrowRight />}
			>
				{t('common:deposits.modal.addAll')}
			</Button>
			<Button
				size="small"
				variant="contained"
				onClick={addHighlighted}
				disabled={!highlightedAvailable.length}
				startIcon={<ChevronRight />}
			>
				{t('common:ui.buttons.add')}
			</Button>
			<Button
				size="small"
				variant="contained"
				color="error"
				onClick={removeHighlighted}
				disabled={!highlightedSelected.length}
				startIcon={<ChevronLeft />}
			>
				{t('common:ui.buttons.remove')}
			</Button>
			<Button
				size="small"
				variant="outlined"
				color="error"
				onClick={removeAll}
				disabled={!selected.length}
				startIcon={<KeyboardDoubleArrowLeft />}
			>
				{t('common:deposits.modal.removeAll')}
			</Button>
		</Stack>
	);

	return (
		<Box>
			<Typography variant="subtitle1" fontWeight={700}>
				{title}
			</Typography>
			{hint ? (
				<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
					{hint}
				</Typography>
			) : null}
			<Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'stretch' }}>
				<PickTable
					label={availableLabel}
					items={available}
					empty={emptyAvailable}
					columns={columns}
					highlighted={highlightLeft}
					onToggle={(item) => toggle('left', item)}
					onMove={(item) => onAdd([item])}
				/>
				{transferButtons}
				<PickTable
					label={selectedLabel}
					items={selected}
					empty={emptySelected}
					columns={columns}
					highlighted={highlightRight}
					onToggle={(item) => toggle('right', item)}
					onMove={(item) => onRemove([item])}
				/>
			</Stack>
		</Box>
	);
};

export const moneyColumns = (
	t: (key: string) => string,
	extra: Column[] = [],
): Column[] => [
	{
		key: 'date',
		header: t('common:tables.date'),
		render: (item) => (item.date ? Util.formatDate(item.date, 'medium') : '—'),
	},
	{
		key: 'mode',
		header: t('common:tables.mode'),
		render: (item) => item.mode_name || '—',
	},
	...extra,
	{
		key: 'amount',
		header: t('common:tables.amount'),
		align: 'right',
		render: (item) => Util.formatNumber(item.amount),
	},
];

export default DepositPickList;
